/* Lightweight token + API client for the client portal.
 *
 * The portal lives in its own folder and (deliberately) doesn't import
 * from the intranet's Redux store, RTK Query slice, or auth feature.
 * This module is the entire portal "client" — store/retrieve the
 * session JWT in localStorage, and wrap fetch with the Authorization
 * header so portal pages aren't repeating boilerplate.
 *
 * When the portal eventually splits into its own deployable bundle this
 * file moves with the rest of `portal/` unchanged.
 */

import { TOO_LARGE_MESSAGE } from './uploadLimits';

const TOKEN_STORAGE_KEY = 'pca-portal-token';
// Marker set when an employee is "logged in as" a client, so the dashboard can
// show a banner + a way back. Holds { target_email, target_name }. Mirrors the
// B2B surface's `pca-b2b-impersonator`.
const IMPERSONATOR_STORAGE_KEY = 'pca-portal-impersonator';

export function setPortalToken(token) {
  try {
    if (token) window.localStorage.setItem(TOKEN_STORAGE_KEY, token);
    else window.localStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch { /* localStorage unavailable — silently no-op */ }
}

export function getPortalToken() {
  try { return window.localStorage.getItem(TOKEN_STORAGE_KEY); }
  catch { return null; }
}

export function clearPortalToken() {
  setPortalToken(null);
}

export function setPortalImpersonator(info) {
  try {
    if (info) window.localStorage.setItem(IMPERSONATOR_STORAGE_KEY, JSON.stringify(info));
    else window.localStorage.removeItem(IMPERSONATOR_STORAGE_KEY);
  } catch { /* localStorage unavailable — silently no-op */ }
}

export function getPortalImpersonator() {
  try {
    const raw = window.localStorage.getItem(IMPERSONATOR_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export function clearPortalImpersonator() {
  setPortalImpersonator(null);
}

/**
 * Authenticated fetch. Adds Authorization: Bearer <token> if a token is
 * stored, surfaces non-2xx as thrown errors with `.status` + `.detail`,
 * and JSON-parses responses (returns null for 204).
 *
 * On 401 we clear the token — caller should redirect to /portal/login.
 */
export async function portalFetch(path, options = {}) {
  const headers = new Headers(options.headers || {});
  const token = getPortalToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);
  // Don't force JSON content-type on FormData uploads — the browser sets
  // multipart boundaries automatically.
  const isForm = options.body instanceof FormData;
  if (!isForm && options.body !== undefined && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  const res = await fetch(path, { ...options, headers });
  if (res.status === 401) {
    clearPortalToken();
    const err = new Error('Session expired. Please sign in again.');
    err.status = 401;
    throw err;
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    // 413 comes from nginx, not the API, so it has an HTML body and no
    // `detail` to show — without this the client would get the bare
    // "Request failed (413)." fallback for the commonest upload mistake.
    const fallback = res.status === 413
      ? TOO_LARGE_MESSAGE
      : `Request failed (${res.status}).`;
    const err = new Error(body?.detail || fallback);
    err.status = res.status;
    err.detail = body?.detail;
    throw err;
  }
  if (res.status === 204) return null;
  return res.json();
}
